/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetMarketMakerMetricsOptions,
  GetMarketMakerMetricsStatus200,
  GetMarketMakerMetricsStatus400,
  GetMarketMakerMetricsStatus401,
  GetMarketMakerMetricsStatus403,
  GetMarketMakerMetricsStatus404,
  GetMarketMakerMetricsStatus429,
  GetMarketMakerMetricsStatus500,
} from '../types/GetMarketMakerMetrics'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getMarketMakerMetrics } from '../clients/getMarketMakerMetrics'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getMarketMakerMetricsQueryKey = ({
  path,
  query,
}: Omit<GetMarketMakerMetricsOptions, 'headers'>) =>
  [
    { url: '/v1/market-makers/:market_maker_id/metrics', params: path },
    ...(query ? [query] : []),
  ] as const

type GetMarketMakerMetricsQueryKey = ReturnType<typeof getMarketMakerMetricsQueryKey>

export function getMarketMakerMetricsQueryOptions(
  { path, query }: GetMarketMakerMetricsOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getMarketMakerMetricsQueryKey({ path, query })
  return queryOptions<
    GetMarketMakerMetricsStatus200,
    ResponseErrorConfig<
      | GetMarketMakerMetricsStatus400
      | GetMarketMakerMetricsStatus401
      | GetMarketMakerMetricsStatus403
      | GetMarketMakerMetricsStatus404
      | GetMarketMakerMetricsStatus429
      | GetMarketMakerMetricsStatus500
    >,
    GetMarketMakerMetricsStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return getMarketMakerMetrics({
        ...config,
        path,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Admins and the market maker's own user. Buckets are UTC-aligned. Without order_book_id the points cover the assigned books; each book is queried on its own and its range may cover at most 3 days of minutes, 90 days of hours or 400 days of days. Depth values are the minimum observed in the bucket, while the KPI report averages each minute's minimum over the window, so a day here can read lower than /kpis?window=day. avg_spread is weighted by two-sided seconds; volumes are sums.
 * @summary Market maker measurements as a time series
 * {@link /v1/market-makers/:market_maker_id/metrics}
 */
export function useGetMarketMakerMetrics<
  TData = GetMarketMakerMetricsStatus200,
  TQueryData = GetMarketMakerMetricsStatus200,
  TQueryKey extends QueryKey = GetMarketMakerMetricsQueryKey,
>(
  {
    path,
    query,
  }: {
    path: GetMarketMakerMetricsOptions['path'] | (() => GetMarketMakerMetricsOptions['path'])
    query: GetMarketMakerMetricsOptions['query'] | (() => GetMarketMakerMetricsOptions['query'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        GetMarketMakerMetricsStatus200,
        ResponseErrorConfig<
          | GetMarketMakerMetricsStatus400
          | GetMarketMakerMetricsStatus401
          | GetMarketMakerMetricsStatus403
          | GetMarketMakerMetricsStatus404
          | GetMarketMakerMetricsStatus429
          | GetMarketMakerMetricsStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = {
    path: typeof path === 'function' ? path() : path,
    query: typeof query === 'function' ? query() : query,
  }
  const queryKey = resolvedOptions?.queryKey ?? getMarketMakerMetricsQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...getMarketMakerMetricsQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<
      | GetMarketMakerMetricsStatus400
      | GetMarketMakerMetricsStatus401
      | GetMarketMakerMetricsStatus403
      | GetMarketMakerMetricsStatus404
      | GetMarketMakerMetricsStatus429
      | GetMarketMakerMetricsStatus500
    >
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
